import {GridCell, GridCellKind} from "@glideapps/glide-data-grid";
import type {TransportAssocEntity, TransportCategory, TransportEntity} from "@ots-frontend/ots-data";
import {DataGridColumnDefinition} from "../DataGrid/useDataGridColumnLayout";
import {getFormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";

type TicketColumn = DataGridColumnDefinition<TransportAssocEntity> & {width: number};
type Value = string | number | undefined;
type TransportField = readonly [code: string, label: string, field: keyof TransportEntity, kind?: "number" | "date"];

/** API fields shared by pickup and delivery; use stable, side-qualified IDs. */
const TRANSPORT_FIELDS: readonly TransportField[] = [
  ["CONTAINER", "Container", "containerName"],
  ["SITE", "Site", "siteName"],
  ["SITE_COMPANY", "Site Company", "siteCompanyName"],
  ["QUANTITY", "Qty", "quantityDecimal", "number"],
  ["SERVICE_TYPE", "Service Type", "serviceTypeName"],
  ["WATER_TYPE", "Freight Type", "waterTypeName"],
  ["TICKET_NUMBER", "Ticket Number", "ticketNumber"],
  ["REGULATORY_NUMBER", "Regulatory Number", "regulatoryNumber"],
  ["UOM", "UOM", "quantityUnitOfMeasure"],
  ["CREATE_DTTM", "Date", "createDttm", "date"],
  ["IN_DTTM", "In", "inDttm", "date"],
  ["OUT_DTTM", "Out", "outDttm", "date"],
  ["LAST_MODIFIED_DTTM", "Last Modified", "lastModifiedDttm", "date"],
  ["SITE_LEASE_ID", "Lease ID", "leaseId"],
  ["METER_START", "Meter Start", "meterReadingStart", "number"],
  ["METER_END", "Meter End", "meterReadingEnd", "number"],
  ["OBS_GRAVITY", "Obs. Gravity", "observedGravity", "number"],
  ["OBS_TEMP", "Obs. Temp.", "observedTemperature", "number"],
  ["CORRECTED_GRAVITY", "Corrected Gravity", "correctedGravity", "number"],
  ["BS_W_PERCENT", "BS&W %", "bswPercentage", "number"],
  ["NET_QTY", "Net Qty", "netQuantity", "number"],
  ["TOP_TEMP", "Top Temp.", "topTemperature", "number"],
  ["BOTTOM_TEMP", "Bottom Temp.", "bottomTemperature", "number"],
  ["SEAL_OFF", "Seal Off", "sealOff"],
  ["SEAL_ON", "Seal On", "sealOn"],
  ["SEAL_2_OFF", "Seal 2 Off", "seal2Off"],
  ["SEAL_2_ON", "Seal 2 On", "seal2On"],
  ["SEAL_3_OFF", "Seal 3 Off", "seal3Off"],
  ["SEAL_3_ON", "Seal 3 On", "seal3On"],
  ["WELL", "Well", "wellName"],
];

const GAUGE_FIELDS = [
  ["TOP_GAUGE", "Top Gauge", "CONTAINER_DEFAULT", "topValue"],
  ["BOTTOM_GAUGE", "Bottom Gauge", "CONTAINER_DEFAULT", "bottomValue"],
  ["TOP_OIL_GAUGE", "Top Oil Gauge", "OIL", "topValue"],
  ["BOTTOM_OIL_GAUGE", "Bottom Oil Gauge", "OIL", "bottomValue"],
  ["TOP_BS_W_BOTTOMS", "Top BS&W Bottoms", "BSW", "topValue"],
  ["BOTTOM_BS_AND_W_BOTTOMS", "Bottom BS&W Bottoms", "BSW", "bottomValue"],
] as const;

// Status actions and multi-value summaries have no single scalar sort value.
const UNSORTABLE_COLUMN_IDS = new Set([
  "STATUS",
  ...["PICKUP", "DELIVERY"].flatMap((side) => [
    ...GAUGE_FIELDS.map(([code]) => `${side}_${code}`),
    `${side}_METER_READING`,
  ]),
]);

export const STANDARD_TICKET_COLUMN_IDS = [
  "RESOURCE_ID",
  "STATUS",
  "REQUEST",
  "PERSON",
  "VEHICLE_AND_TRAILER",
  "PICKUP_CREATE_DTTM",
  "PICKUP_SITE",
  "PICKUP_CONTAINER",
  "PICKUP_QUANTITY",
  "DELIVERY_CREATE_DTTM",
  "DELIVERY_SITE",
  "DELIVERY_CONTAINER",
  "DELIVERY_QUANTITY",
  "SERVICE_TYPE",
  "FREIGHT_TYPE",
];

export function getDefaultTicketColumnIds(category?: TransportCategory): string[] {
  const ids = [...STANDARD_TICKET_COLUMN_IDS];
  if (category?.code === "OIL")
    ids.splice(9, 0, "PICKUP_NET_QTY", "PICKUP_OBS_GRAVITY", "PICKUP_OBS_TEMP", "PICKUP_BS_W_PERCENT");
  return ids;
}

export function createTicketDataGridColumns(
  category?: TransportCategory,
  legacyWidths?: (number | undefined)[]
): TicketColumn[] {
  const columns: TicketColumn[] = [];
  const numberFormat = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2});
  const textCell = (value: Value): GridCell => ({
    kind: GridCellKind.Text,
    data: value === undefined ? "" : String(value),
    displayData: value === undefined ? "" : String(value),
    allowOverlay: false,
  });
  const add = (
    id: string,
    title: string,
    value: (ticket: TransportAssocEntity) => Value,
    kind?: "number" | "date",
    group?: string
  ) => {
    columns.push({
      id,
      title,
      group,
      width: kind === "date" ? 160 : 120,
      hasMenu: true,
      getSortValue: (ticket) => {
        const raw = value(ticket);
        return kind === "number" || kind === "date" ? (typeof raw === "number" ? raw : undefined) : raw;
      },
      getContent: (ticket) => {
        const data = value(ticket);
        if (kind === "number")
          return {
            kind: GridCellKind.Number,
            data: typeof data === "number" ? data : undefined,
            displayData: typeof data === "number" ? numberFormat.format(data) : "",
            allowOverlay: false,
          };
        return textCell(kind === "date" ? (typeof data === "number" ? getFormattedDateTime(data) : undefined) : data);
      },
    });
  };
  const primary = (ticket: TransportAssocEntity) => ticket.pickupTransport ?? ticket.deliveryTransport;
  const gauge = (transport: TransportEntity | undefined, type: string, end: "topValue" | "bottomValue") =>
    transport?.gauges
      ?.filter((gauge) => gauge.type?.code === type)
      .map((gauge) => {
        const value = gauge[end];
        return value === undefined
          ? ""
          : `${gauge.containerName ? gauge.containerName + ": " : ""}${numberFormat.format(value)}`;
      })
      .filter(Boolean)
      .join("; ");

  add("RESOURCE_ID", "ID", (ticket) => ticket.resourceIdentifiers.join(", "));
  columns.push({
    id: "STATUS",
    title: "",
    columnLabel: "Messages / Validation",
    width: 75,
    hasMenu: true,
    getContent: (ticket) => {
      const hasMessages = ticket.messages.length > 0;
      return {
        kind: GridCellKind.Image,
        data: [
          hasMessages ? import.meta.env.BASE_URL + "message.svg" : undefined,
          ticket.dispatchValidatedDttm ? import.meta.env.BASE_URL + "circle-check.svg" : undefined,
        ].filter((value): value is string => value !== undefined),
        allowOverlay: false,
        allowAdd: false,
        cursor: hasMessages ? "pointer" : undefined,
      };
    },
  });
  add("REQUEST", "Request", (ticket) => {
    let workOrder = ticket.pickupTransport?.workOrderNumber ?? ticket.deliveryTransport?.workOrderNumber;
    let request;
    for (const dispatch of ticket.dispatches ?? []) {
      workOrder ??= dispatch.workOrderNumber;
      request ??= dispatch.dispatchRequest;
    }
    let display = workOrder ?? "";
    if (request) {
      display += ` (${request.displayName}`;
      if (request.externalId && request.externalSystem) {
        display += ` - Ext. ${request.externalSystem}`;
        if (request.externalId !== workOrder) display += `: ${request.externalId}`;
      }
      display += ")";
    }
    return display;
  });
  add("PERSON", "Driver", (ticket) => primary(ticket)?.personName);
  add("VEHICLE", "Vehicle", (ticket) => primary(ticket)?.vehicleName);
  add("TRAILER", "Trailer", (ticket) => primary(ticket)?.trailerName);
  add(
    "VEHICLE_AND_TRAILER",
    "Vehicle / Trailer",
    (ticket) => `${primary(ticket)?.vehicleName ?? "-"} / ${primary(ticket)?.trailerName ?? "-"}`
  );
  add("TRUCK_COMPANY", "Truck Company", (ticket) => primary(ticket)?.trailerCompanyName);
  add("WORK_ORDER_NUMBER", "Work Order Number", (ticket) => primary(ticket)?.workOrderNumber);
  add("WORK_ORDER_NUMBER_INTERNAL", "Internal Work Order Number", (ticket) => primary(ticket)?.internalWorkOrderNumber);
  add("SERVICE_TYPE", "Service Type", (ticket) => ticket.serviceTypeName, undefined, "Load");
  add("FREIGHT_TYPE", "Freight Type", (ticket) => primary(ticket)?.waterTypeName, undefined, "Load");

  for (const side of ["PICKUP", "DELIVERY"] as const) {
    const group = side === "PICKUP" ? "Pickup" : "Delivery";
    const prefix = side === "PICKUP" ? "P/U" : "Del.";
    const transport = (ticket: TransportAssocEntity) =>
      side === "PICKUP" ? ticket.pickupTransport : ticket.deliveryTransport;
    for (const [code, label, field, kind] of TRANSPORT_FIELDS) {
      add(
        `${side}_${code}`,
        `${prefix} ${label}`,
        (ticket) => {
          const value = transport(ticket)?.[field];
          return typeof value === "string" || typeof value === "number" ? value : undefined;
        },
        kind,
        group
      );
    }
    for (const [code, label, type, end] of GAUGE_FIELDS)
      add(`${side}_${code}`, `${prefix} ${label}`, (ticket) => gauge(transport(ticket), type, end), undefined, group);
    for (const [code, label, field] of [
      ["SEAL_VALVE_TYPE", "Seal Valve Type", "sealValveType"],
      ["SEAL_2_VALVE_TYPE", "Seal 2 Valve Type", "seal2ValveType"],
      ["SEAL_3_VALVE_TYPE", "Seal 3 Valve Type", "seal3ValveType"],
    ] as const)
      add(
        `${side}_${code}`,
        `${prefix} ${label}`,
        (ticket) => {
          const valve = transport(ticket)?.[field];
          return valve?.displayValue?.defaultMessage ?? valve?.code;
        },
        undefined,
        group
      );
    add(
      `${side}_REJECTION_TYPE`,
      `${prefix} Rejection Type`,
      (ticket) => transport(ticket)?.rejectionType?.displayValue.defaultMessage,
      undefined,
      group
    );
    add(
      `${side}_GEOFENCE`,
      `${prefix} Geofence`,
      (ticket) => transport(ticket)?.geofenceEvent?.siteName,
      undefined,
      group
    );
    add(
      `${side}_METER_READING`,
      `${prefix} Meter Reading`,
      (ticket) => {
        const current = transport(ticket);
        return [current?.meterReadingStart, current?.meterReadingEnd]
          .filter((value): value is number => value !== undefined)
          .map((value) => numberFormat.format(value))
          .join(" – ");
      },
      undefined,
      group
    );
  }
  add("DISPOSAL_QUANTITY", "Disposal Qty", (ticket) => ticket.deliveryTransport?.disposalQty, "number", "Delivery");

  const defaults = getDefaultTicketColumnIds(category);
  const byId = new Map(columns.map((column) => [column.id, column]));
  return [
    ...defaults.flatMap((id) => {
      const column = byId.get(id);
      return column ? [column] : [];
    }),
    ...columns.filter((column) => !defaults.includes(column.id)),
  ].map((column) => {
    const index = defaults.indexOf(column.id);
    return {
      ...column,
      sortable: !UNSORTABLE_COLUMN_IDS.has(column.id),
      hasMenu: !UNSORTABLE_COLUMN_IDS.has(column.id),
      getSortValue: UNSORTABLE_COLUMN_IDS.has(column.id) ? undefined : column.getSortValue,
      defaultVisible: index !== -1,
      width: index !== -1 ? legacyWidths?.[index] ?? column.width : column.width,
    };
  });
}
